import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiFileText, FiShoppingBag, FiImage, FiVideo, FiPlus, FiLayers, FiMessageSquare } from "react-icons/fi";
import api from "../utils/api";
import Loader from "../components/Loader";

export default function Dashboard() {
  const [stats, setStats] = useState({
    blogs: 0,
    products: 0,
    gallery: 0,
    videos: 0,
    services: 0,
    testimonials: 0,
    pendingReviews: 0,
    messages: 0,
    unread: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/blogs/admin/all"),
      api.get("/products/admin/all"),
      api.get("/gallery", { params: { page: 1, limit: 1 } }),
      api.get("/videos"),
      api.get("/services/admin/all"),
      api.get("/testimonials/admin/all"),
      api.get("/contact"),
    ])
      .then(([blogsRes, productsRes, galleryRes, videosRes, servicesRes, testimonialsRes, contactsRes]) => {
        const unread = contactsRes.data.filter((c) => !c.read).length;
        const pendingReviews = testimonialsRes.data.filter((t) => !t.approved).length;
        setStats({
          blogs: blogsRes.data.length,
          products: productsRes.data.length,
          gallery: galleryRes.data.total ?? 0,
          videos: videosRes.data.length,
          services: servicesRes.data.length,
          testimonials: testimonialsRes.data.length,
          pendingReviews,
          messages: contactsRes.data.length,
          unread,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  const cards = [
    { label: "Services", value: stats.services, icon: <FiLayers />, color: "bg-purple-600", link: "/admin/services" },
    { label: "Total Blog Posts", value: stats.blogs, icon: <FiFileText />, color: "bg-saffron-500", link: "/admin/blogs" },
    { label: "Marketplace Products", value: stats.products, icon: <FiShoppingBag />, color: "bg-maroon-700", link: "/admin/products" },
    { label: "Gallery Items", value: stats.gallery, icon: <FiImage />, color: "bg-gold-600", link: "/admin/gallery" },
    { label: "Videos & Reviews", value: stats.videos, icon: <FiVideo />, color: "bg-red-500", link: "/admin/videos" },
    { label: "Testimonials", value: stats.testimonials, icon: <FiMessageSquare />, color: "bg-teal-600", link: "/admin/testimonials" },
  ];

  return (
    <div>
      <h1 className="font-heading text-2xl text-maroon-800 mb-1">Welcome to the Admin Panel</h1>
      <p className="text-gray-500 mb-8">Manage your blog posts, marketplace products and contact messages here.</p>

      {/* Pending review alert */}
      {stats.pendingReviews > 0 && (
        <Link
          to="/admin/testimonials"
          className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-8 hover:bg-amber-100 transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <FiMessageSquare />
          </div>
          <div>
            <p className="font-heading text-maroon-800 text-sm">
              {stats.pendingReviews} new review{stats.pendingReviews > 1 ? "s" : ""} awaiting approval
            </p>
            <p className="text-xs text-amber-700">Click here to review and publish them.</p>
          </div>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 mb-10">
        {cards.map((c) => (
          <Link key={c.label} to={c.link} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className={`w-11 h-11 rounded-xl ${c.color} text-white flex items-center justify-center text-lg mb-4`}>{c.icon}</div>
            <p className="text-2xl font-bold text-maroon-800">{c.value}</p>
            <p className="text-sm text-gray-500">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Link to="/admin/services/new" className="bg-purple-50 border-2 border-dashed border-purple-300 rounded-2xl p-6 flex items-center gap-4 hover:bg-purple-100 transition-colors">
          <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center text-xl"><FiPlus /></div>
          <div>
            <p className="font-heading text-maroon-800">Add New Service</p>
            <p className="text-sm text-gray-500">Add pooja or astrology services</p>
          </div>
        </Link>
        <Link to="/admin/blogs/new" className="bg-saffron-50 border-2 border-dashed border-saffron-300 rounded-2xl p-6 flex items-center gap-4 hover:bg-saffron-100 transition-colors">
          <div className="w-12 h-12 rounded-full bg-saffron-500 text-white flex items-center justify-center text-xl"><FiPlus /></div>
          <div>
            <p className="font-heading text-maroon-800">Write New Blog Post</p>
            <p className="text-sm text-gray-500">Share astrology, vastu or pooja insights</p>
          </div>
        </Link>
        <Link to="/admin/products/new" className="bg-maroon-50 border-2 border-dashed border-maroon-300 rounded-2xl p-6 flex items-center gap-4 hover:bg-maroon-100 transition-colors">
          <div className="w-12 h-12 rounded-full bg-maroon-700 text-gold-100 flex items-center justify-center text-xl"><FiPlus /></div>
          <div>
            <p className="font-heading text-maroon-800">Add New Product</p>
            <p className="text-sm text-gray-500">Add puja samagri, gemstones, rudraksha etc.</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
